import { vars } from '@styles/vanilla-extract/theme.css';
import { Trans, useTranslation } from 'react-i18next';
import React from 'react';
import RatioSingleBarChart, {
	RatioSingleBarChartProps,
} from '@components/report/chart/ratio-single-bar-chart/RatioSingleBarChart';
import TriangleChart, {
	TriangleChartProps,
} from '@components/report/chart/triangle-chart/TriangleChart';
import ColoredSection from '@components/report/colored-section/ColoredSection';

import ReportCard from '@components/report/report-card/ReportCard';

import ReportDoubleCard from '@components/report/report-double-card/ReportDoubleCard';
import { twoDivisionGridClass } from '@components/report/style.css';

import { containerClass, headContainerClass } from './style.css';

export interface ContentYtProps {
	bannerHashtags?: string[];
	contentType?: RatioSingleBarChartProps;
	averageVideoView?: TriangleChartProps;
	averageVideoEngagement?: TriangleChartProps;
	averageShortsView?: TriangleChartProps;
	averageShortsEngagement?: TriangleChartProps;

	isLoading?: boolean;
	isError?: boolean;
	lackOfPermission?: boolean;
}

const ContentYt = ({
	contentType,
	averageVideoView,
	averageVideoEngagement,
	averageShortsView,
	averageShortsEngagement,

	isLoading,
	isError,
	lackOfPermission,
	...props
}: ContentYtProps) => {
	const { t } = useTranslation('infReportYoutube');
	const status = { isLoading, isError, lackOfPermission };

	const bannerHashtags =
		isLoading || isError
			? new Array(5).fill('-')
			: lackOfPermission
			? new Array(5).fill('example')
			: props.bannerHashtags && props.bannerHashtags.length > 0
			? props.bannerHashtags
			: t('features.report.section-content.yt.content-yt.banner-hashtags.no-data');

	const isValidHashtags = Array.isArray(bannerHashtags) && bannerHashtags.length > 0;

	const bannerHashtagsString = isValidHashtags
		? bannerHashtags.map((item) => `#${item}`).join(', ')
		: bannerHashtags;

	return (
		<div className={containerClass}>
			<div className={headContainerClass}>
				<ColoredSection
					tooltip={t(
						'features.report.section-content.yt.content-yt.banner-hashtags.tooltip',
					)}
					{...status}
				>
					<Trans
						t={t}
						i18nKey="features.report.section-content.yt.content-yt.banner-hashtags.description"
						components={
							isValidHashtags
								? [<span key={'hashtags'}>{bannerHashtagsString}</span>]
								: [<>{bannerHashtagsString}</>]
						}
					/>
				</ColoredSection>

				<ReportCard
					label={t('features.report.section-content.yt.content-yt.content-type.label')}
					tooltip={t(
						'features.report.section-content.yt.content-yt.content-type.tooltip',
					)}
					lackOfPermission={lackOfPermission}
					height={180}
					align="horizontal"
				>
					<RatioSingleBarChart
						subDesc={t(
							'features.report.section-content.yt.content-yt.content-type.ratio-single-bar-chart.sub-description',
						)}
						items={[
							{
								label: t(
									'features.report.section-content.yt.content-yt.content-type.ratio-single-bar-chart.video',
								),
								value: '-%',
								color: vars.global.colors.primary[40],
							},
							{
								label: t(
									'features.report.section-content.yt.content-yt.content-type.ratio-single-bar-chart.shorts',
								),
								value: '-%',
								color: vars.global.colors.primary[60],
							},
						]}
						isLegend
						align="horizontal"
						{...contentType}
						{...status}
					/>
				</ReportCard>

				<div className={twoDivisionGridClass}>
					<ReportDoubleCard>
						<ReportCard
							label={t(
								'features.report.section-content.yt.content-yt.content-type.avg-video-view.label',
							)}
							tooltip={t(
								'features.report.section-content.yt.content-yt.content-type.avg-video-view.tooltip',
							)}
							height={296}
							lackOfPermission={lackOfPermission}
						>
							<TriangleChart
								size="small"
								graphAlign="left"
								desc
								{...averageVideoView}
								{...status}
							/>
						</ReportCard>
						<ReportCard
							label={t(
								'features.report.section-content.yt.content-yt.content-type.avg-video-engagement.label',
							)}
							tooltip={t(
								'features.report.section-content.yt.content-yt.content-type.avg-video-engagement.tooltip',
							)}
							height={296}
							lackOfPermission={lackOfPermission}
						>
							<TriangleChart
								size="small"
								graphAlign="left"
								desc
								{...averageVideoEngagement}
								{...status}
							/>
						</ReportCard>
					</ReportDoubleCard>

					<ReportDoubleCard>
						<ReportCard
							label={t(
								'features.report.section-content.yt.content-yt.content-type.avg-shorts-view.label',
							)}
							tooltip={t(
								'features.report.section-content.yt.content-yt.content-type.avg-shorts-view.tooltip',
							)}
							height={296}
							lackOfPermission={lackOfPermission}
						>
							<TriangleChart
								size="small"
								graphAlign="left"
								desc
								{...averageShortsView}
								{...status}
							/>
						</ReportCard>
						<ReportCard
							label={t(
								'features.report.section-content.yt.content-yt.content-type.avg-shorts-engagement.label',
							)}
							tooltip={t(
								'features.report.section-content.yt.content-yt.content-type.avg-shorts-engagement.tooltip',
							)}
							height={296}
							lackOfPermission={lackOfPermission}
						>
							<TriangleChart
								size="small"
								graphAlign="left"
								desc
								{...averageShortsEngagement}
								{...status}
							/>
						</ReportCard>
					</ReportDoubleCard>
				</div>
			</div>
		</div>
	);
};

export default ContentYt;
